import styled from '@emotion/styled';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import IconBreathe from '../../../../../assets/images/roofing/icons/icon_breathe.svg';
import mdms_api from '../../../../mdms_api';
import FormModal from '../../../ComponentLibrary/forms/oc-form-modal';
import { H3 } from '../../../ComponentLibrary/text-elements/oc-h';
import RichText from '../../../OC/oc-rich-text';
import ColorSwatch from '../ColorSwatch';
import ShingleColorTitle from '../ShingleColorTitle';
import ShinglePreview from '../ShinglePreview';
import RoofingComponentsModal from '../modals/RoofingComponentsModal';

const IconSeal =
  'https://imagecdn.owenscorning.com/ocimages/image/upload/v1661545059/roofing/widgets/quoting-widget/icons/icon-seal.svg';
const IconDefend =
  'https://imagecdn.owenscorning.com/ocimages/image/upload/v1661545059/roofing/widgets/quoting-widget/icons/icon-defend.svg';

const Container = styled.div`
  display: inline-block;
  margin-bottom: 34px;
  position: relative;
  width: 100%;
`;

const PageDivider = styled.div`
  border-bottom: 2px solid #000000;
  margin-bottom: 50px;
`;

const Title = styled(H3)`
  margin-bottom: 16px;
  width: 100%;
`;

const SubCta = styled.a`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  margin-bottom: 1.75em;
  position: relative;
  text-decoration: none;
  transition: all 0.35s ease-in-out;

  &:after {
    content: '';
    font-family: icomoon;
    margin-left: 12px;
    position: relative;
  }
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 40px;
  position: relative;
  gap: 32px;
`;

const ComponentsHeader = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
`;

const SelectorContent = styled.div`
  margin-bottom: 1em;
  position: relative;
  width: 46%;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ShingleInfo = styled(SelectorContent)`
  display: inline-block;
`;

const ShingleColors = styled(SelectorContent)`
  align-content: flex-start;
  align-items: flex-start;
  display: inline-flex;
  flex-wrap: wrap;
  flex-grow: 1;
`;

const ComponentItem = styled.div`
  display: inline-flex;
  margin-bottom: 32px;
  position: relative;
  width: 46%;

  &:nth-of-type(odd) {
    padding-left: 16px;

    @media screen and (max-width: 768px) {
      padding-left: 0px;
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ComponentImage = styled.div`
  background-image: url('${props => props.imageUrl}');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: inline-block;
  flex-shrink: 0;
  height: 72px;
  margin-right: 16px;
  position: relative;
  width: 72px;
`;

const ComponentCopy = styled.div`
  display: inline-block;
  position: relative;
`;

const ComponentBadge = styled.div`
  align-items: center;
  background-color: #d40f7d;
  color: #ffffff;
  display: inline-flex;
  font-family: Oswald;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 6px;
  padding: 0 4px;
  position: relative;
  text-transform: uppercase;
  width: 93px;

  &.defend {
    background-color: #6d6d6d;
  }

  &.breathe {
    background-color: #bcbcbc;
    color: #000000;
  }
`;

const ComponentBadgeIcon = styled.div`
  background-image: url('${props => props.url}');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: inline-block;
  height: 16px;
  margin-right: 5px;
  position: relative;
  width: 16px;
`;

const ComponentTitle = styled(H3)`
  &.title {
    font-size: 18px;
    margin-bottom: 8px;
  }
`;

const ComponentDescription = styled.p`
  margin: 0 0 8px;
`;

const durationDescriptionDefault = {
  ops: [
    {
      insert:
        "When you want the ultimate protection and impressive curb appeal, you'll want Duration® Shingles. They are specially formulated on a TruDefinition® color platform that gives them dramatic color contrast and dimension in a wide range of popular colors. Beyond the great look, Duration® Shingles are Rated Class 3 for Impact Resistance",
    },
    {
      insert: '1',
      attributes: {
        script: 'super',
      },
    },
    {
      insert: ' and come with the advanced performance of patented ',
    },
    {
      insert: 'SureNail® Technology',
      attributes: {
        link: '/roofing/surenail?enable=contractor_referral',
      },
    },
    {
      insert: '—with Triple Layer Protection® in the nailing zone for superior holding power. ',
    },
    {
      insert: 'We proved it in testing.',
      attributes: {
        link: '/en-us/roofing/nailing-zone-performance?enable=contractor_referral',
      },
    },
    {
      insert: '\n',
    },
  ],
};

const addReferralParamToLink = link => {
  const baseUrl = new URL(location.origin);
  const linkUrl = new URL(link, baseUrl);

  linkUrl.searchParams?.append('enable', 'contractor_referral');
  return linkUrl.pathname + linkUrl.search;
};

const ShingleColorSelector = ({
  blurValidation,
  changeValidation,
  fields,
  values,
  touched,
  errors,
  submissionError,
  themeColor,
  configuration,
  subCta,
}) => {
  const [loading, setLoading] = useState(true);
  const [shingleColors, setShingleColors] = useState([]);
  const [selectedImageView, setSelectedImageView] = useState('house');
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [shingleName, setShingleName] = useState('TruDefinition® Duration®');
  const [shingleDescription, setShingleDescription] = useState(durationDescriptionDefault);
  const [openModal, setOpenModal] = useState();
  const icons = { seal: IconSeal, defend: IconDefend, breathe: IconBreathe };
  const roofingComponentData = [
    configuration['ice-water-barrier'],
    configuration['hip-ridge-shingles'],
    configuration.underlayment,
    configuration['starter-shingles'],
    configuration['exhaust-ventilation'],
  ];
  // Hardcoded until customization functionality is determined;
  // should be converted to prop passed from widget configuration
  const shingleUid = configuration.allowedShingle ? configuration.allowedShingle : 'trudefinition-duration';
  const colorField = fields[0];
  let navigation = 0;

  const imageViewToggle = selectedView => {
    setSelectedImageView(selectedView);
  };

  const selectSwatch = event => {
    const { value } = event.target;
    const selectedColor = shingleColors.find(color => color.uid === value);
    const { name } = selectedColor;
    const thumbnail = selectedColor.swatch_160x160_url;
    const thumbnail2 = selectedColor.swatch_768x485_url;
    const thumbnail3 = selectedColor.swatch_768x768_url;

    changeValidation(event, { required: colorField.required }, colorField.name, [
      name,
      thumbnail,
      shingleName,
      thumbnail2,
      thumbnail3,
    ]);
    setSelectedSwatch(selectedColor);
  };

  const filterColors = colors =>
    // TODO: We have pivoted and for MVP we are no longer allowing contractor to specify the available
    // colors they want to provide. This may change in the future.
    // if (configuration?.allowedShingles?.[shingleUid]?.length > 0) {
    //   return colors.filter(color => configuration.allowedShingles[shingleUid].includes(color.uid));
    // }

    colors;

  const shinglesUrl = () => {
    if (configuration?.serviceArea?.length > 0)
      return `/api/v2/product/shingles/${shingleUid}?zip=${configuration.serviceArea}`;

    return `/api/v2/product/shingles/${shingleUid}`;
  };

  const closeFormModal = () => {
    setOpenModal(false);
  };

  const openFormModal = () => {
    setOpenModal(true);
  };

  useEffect(() => {
    mdms_api
      .get(shinglesUrl())
      .then(response => response.json())
      .then(responseJSON => {
        const colors = responseJSON.data.shingle_colors;
        const { description } = responseJSON.data;
        if (_.isObject(description)) {
          description.ops?.map(op => {
            const link = op.attributes?.link;
            if (!link) return;

            op.attributes.link = addReferralParamToLink(link);
          });
        }
        setShingleName(responseJSON.data.full_proper_name);
        setShingleDescription(description);
        if (colors instanceof Array) {
          let colorData = colors.map(color => ({
            house_1229x768_url2: color.house_1229x768_url2,
            name: color.name,
            swatch_160x160_url: color.swatch_160x160_url,
            swatch_768x485_url: color.swatch_768x485_url,
            swatch_768x768_url: color.swatch_768x768_url,
            uid: color.uid,
          }));

          colorData = filterColors(colorData);
          if (colorData.length > 0) {
            setShingleColors(colorData);
            setSelectedSwatch(colorData[0]);
            values[colorField.name] = [
              colorData[0].uid,
              colorData[0].swatch_160x160_url,
              shingleName,
              colorData[0].swatch_768x485_url,
            ];
          }
          setLoading(false);
        }
      })
      .catch(() => {
        console.log('Error loading shingle colors');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const selecSwatchByButtonPress = event => {
      if (
        (event.code === 'Enter' || event.code === 'Space') &&
        document.activeElement?.control?.name == 'shingle-color'
      ) {
        const { value } = document.activeElement.control;
        const selectedColor = shingleColors.find(color => color.uid === value);
        const { name } = selectedColor;
        const thumbnail = selectedColor.swatch_160x160_url;

        changeValidation(event, { required: colorField.required }, colorField.name, [name, thumbnail]);
        setSelectedSwatch(selectedColor);
      }
    };

    document.addEventListener('keydown', selecSwatchByButtonPress);

    return () => {
      document.removeEventListener('keydown', selecSwatchByButtonPress);
    };
  }, [shingleColors]);

  return (
    <>
      <Container>
        <H3>Shingles</H3>
        {!loading && (
          <SelectorContainer>
            <ShingleInfo>
              <ShinglePreview
                onToggle={imageViewToggle}
                selectedView={selectedImageView}
                shingleColor={selectedSwatch}
              />
            </ShingleInfo>
            <ShingleColors onChange={event => selectSwatch(event)}>
              <ShingleColorTitle colorName={selectedSwatch.name} shingleName={shingleName} />
              {shingleColors.map(color => (
                <ColorSwatch
                  tabIndex={++navigation}
                  colorData={color}
                  key={color.uid}
                  name={colorField.name}
                  selected={color.uid === selectedSwatch.uid}
                  tracking={{
                    'data-track': 'view-product',
                    'data-track-product-color': color.name,
                    'data-track-product-name': `${shingleUid}-${color.uid}`,
                  }}
                />
              ))}
            </ShingleColors>
          </SelectorContainer>
        )}
        <p>
          <strong>{shingleName} Shingles</strong>
        </p>
        <RichText content={shingleDescription} />
      </Container>
      <PageDivider />
      <Container style={{ 'margin-bottom': '18px' }}>
        <ComponentsHeader>
          <H3>Included Roofing Components</H3>
          <SubCta
            data-track={subCta.data && subCta.data.track}
            data-track-destination={subCta.data && subCta.data.trackDestination}
            href={subCta.href || '#'}
            onClick={() => openFormModal()}
            target={subCta.href && '_blank'}
            title={subCta.text}
          >
            {subCta.text}
          </SubCta>
        </ComponentsHeader>
        {roofingComponentData.map(data => {
          if (!data) return;
          if (!data.display) return;
          const { product } = data;
          const description = product?.proper_name;
          const imageUrl = product?.images?.product_thumb?.url;
          const imageAlt = product?.images?.product_thumb?.alt;
          const type = product?.applications[0]?.slug;
          const iconUrl = icons[type];

          return (
            <ComponentItem key={data.uid}>
              <ComponentImage aria-label={imageAlt} role="img" imageUrl={imageUrl} />
              <ComponentCopy>
                <ComponentBadge className={type}>
                  <ComponentBadgeIcon url={iconUrl} />
                  {type}
                </ComponentBadge>
                <ComponentTitle className="title" font="body">
                  {data.category === 'Underlayment' ? `Synthetic ${data.category}` : data.category}
                </ComponentTitle>
                <ComponentDescription>{description}</ComponentDescription>
              </ComponentCopy>
            </ComponentItem>
          );
        })}
      </Container>
      {openModal && (
        <FormModal
          name="What are Roofing Components"
          onClose={() => closeFormModal()}
          open
          modalBodyPosition="absolute"
        >
          <RoofingComponentsModal />
        </FormModal>
      )}
    </>
  );
};

export default ShingleColorSelector;
